/* eslint-disable max-len */
/* eslint-disable @typescript-eslint/no-non-null-assertion */
/* eslint-disable no-underscore-dangle */
import moment from 'moment';
import Delta, { Op } from 'quill-delta';

import { SalesAPIService } from 'shared/api/sales-api.service';
import { AutoboardVehicle } from '../../../src/app/@shared-types/vehicle';
import { AutoScout24APIConfig } from '../../../src/app/@shared-types/api/autoscout24';
import { CommonExportApiData } from '../../../src/app/@shared-types/vehicle/api';
import { DeepPartial } from '../../../src/app/@shared-types';
import { EExternalAPI } from '../../../src/app/@shared-types/api';
import { getAdditionalOptionsAsOps, getModelDataAsOps, getSeriesOptionsAsOps } from '../../../src/app/shared/models/vehicle/description';
import { MobileSellerAPIConfig } from '../../../src/app/@shared-types/api/mobile-de';
import { NumberConversionService } from '../../../shared/converter/number-converter.service';
import { PlatformsAPIService } from '../../../shared/api/platforms-api.service';
import { SettingsAPIService } from '../../../shared/api/settings-api.service';
import { ToastService, ToastType } from '../../../ui-components/toast/toast.service';
import { VehicleExportFrontendRequest } from '../../../src/app/@shared-types/api/export-crud';
import { VehicleFormComponentExitFunction } from '../../../src/app/@types/vehicle';
import LogService from '../../../src/app/shared/services/log';
import { RootScope } from '../../../app.config';
import { AWIAPIAccount, AWIAPIConfig } from '../../../src/app/@shared-types/api/awi';

interface APIAccount {
  name: string,
  id: string,
  publish: boolean,
}

export class OnlinePlatformsFormController {
  private $rootScope: RootScope;

  private exit!: (data: VehicleFormComponentExitFunction) => void;

  private logService: LogService;

  private toastService: ToastService;

  private vehicle!: AutoboardVehicle;

  /** This is the data saved with a `AutoboardVehicle` that contains
   * data that is shared between all API's where vehicles are published at.
   * For example the title, description etc. */
  public formCommonApiData: CommonExportApiData = {
    description: {
      formatted: '',
      raw: {},
    },
    title: '',
  };

  public formData: {
    deliveryDate: string | null,
    deliveryPeriod: string | null,
    onlinePrice: string,
    sending: boolean,
    showDescription: boolean,
  } | null = null;

  public errors = '';

  private settingsApiService: SettingsAPIService;

  private platformsApiService: PlatformsAPIService;

  private numberConversionService: NumberConversionService;

  private salesApiService: SalesAPIService;

  public apiAccounts: {
    mobileSeller: APIAccount[],
    autoScout24: APIAccount[],
    awi: APIAccount[],
    ready: boolean,
  } = {
    mobileSeller: [],
    autoScout24: [],
    awi: [],
    ready: false,
  };

  public addAccount: {
    mobileSeller: string,
    autoScout24: string,
    awi: string,
  } = {
    autoScout24: '',
    mobileSeller: '',
    awi: '',
  };

  /* @ngInject */
  constructor(
    $rootScope: RootScope,
    logService: LogService,
    toastService: ToastService,
    settingsApiService: SettingsAPIService,
    platformsApiService: PlatformsAPIService,
    numberConversionService: NumberConversionService,
    salesApiService: SalesAPIService,
  ) {
    this.$rootScope = $rootScope;
    this.logService = logService;
    this.toastService = toastService;
    this.settingsApiService = settingsApiService;
    this.platformsApiService = platformsApiService;
    this.numberConversionService = numberConversionService;
    this.salesApiService = salesApiService;
  }

  public $onInit = async (): Promise<void> => {
    if (this.vehicle.apiData?.commonExport) this.formCommonApiData = this.vehicle.apiData.commonExport;

    /** Fetch available API account for each `EExportAPI` */
    const enabledApis = this.$rootScope.globals.enabledApis || [];
    if (enabledApis.includes(EExternalAPI.MOBILE_SELLER)) await this.getMobileSellerConfig();
    if (enabledApis.includes(EExternalAPI.AUTOSCOUT24)) await this.getAutoScout24Config();
    if (enabledApis.includes(EExternalAPI.AWI)) await this.getAWIConfig();

    this.apiAccounts.ready = true;

    /** Push data from `commonExport` to our ´formData` to be used in the app */
    const { commonExport } = this.vehicle.apiData || {};
    this.formData = {
      deliveryDate: commonExport?.deliveryDate ? moment(commonExport.deliveryDate).format('YYYY-MM-DD') : null,
      deliveryPeriod: commonExport?.deliveryPeriod ? String(commonExport.deliveryPeriod) : null,
      sending: false,
      showDescription: true,
      onlinePrice: this.numberConversionService.convertFloatToFrontend(this.vehicle.vehicleData.price.onlinePrice || 0),
    };

    const { apiData } = this.vehicle;

    /** Pre-select API accounts, where the current `AutoboardVehicle` is already synced with */

    /** Mobile.de Seller API */
    apiData?.MOBILE_SELLER?.forEach((a) => {
      const apiAccount = this.apiAccounts.mobileSeller.find((a2) => a2.id === a.sellerId);
      if (apiAccount) apiAccount.publish = true;
    });

    /** AutoScout24 */
    apiData?.AUTOSCOUT24?.forEach((a) => {
      const apiAccount = this.apiAccounts.autoScout24.find((a2) => a2.id === a.customerId);
      if (apiAccount) apiAccount.publish = true;
    });

    /** AutoScout24 */
    apiData?.AWI?.forEach((a) => {
      const apiAccount = this.apiAccounts.awi.find((a2) => a2.id === a.accountId);
      if (apiAccount) apiAccount.publish = true;
    });
  };

  /** Toggle the selection of a `Mobile.de Seller API Account` */
  public changeMobileAccountState = (
    sellerId: string,
  ): void => {
    const account = this.apiAccounts.mobileSeller
      .find((a) => a.id === sellerId);

    if (account) account.publish = !account.publish;
  };

  /** Toggle the selection of a `AutoScout24 API Account` */
  public changeAutoScout24AccountState = (
    sellerId: string,
  ): void => {
    const account = this.apiAccounts.autoScout24
      .find((a) => a.id === sellerId);

    if (account) account.publish = !account.publish;
  };

  /** Toggle the selection of a `AWI API Account` */
  public changeAWIAccountState = (
    sellerId: string,
  ): void => {
    const account = this.apiAccounts.awi
      .find((a) => a.id === sellerId);

    if (account) account.publish = !account.publish;
  };

  private getMobileSellerConfig = async (): Promise<void> => {
    const configs = await this.settingsApiService
      .getAPIConfig<MobileSellerAPIConfig[]>(EExternalAPI.MOBILE_SELLER).catch();

    configs[0].accounts.forEach((a) => {
      this.apiAccounts.mobileSeller.push({
        name: a.name,
        id: a.sellerId,
        publish: false,
      });
    });
  };

  private getAutoScout24Config = async (): Promise<void> => {
    const configs = await this.settingsApiService
      .getAPIConfig<AutoScout24APIConfig[]>(EExternalAPI.AUTOSCOUT24).catch();

    configs[0].accounts.forEach((a) => {
      this.apiAccounts.autoScout24.push({
        name: a.name,
        id: a.customerId,
        publish: false,
      });
    });
  };

  private getAWIConfig = async (): Promise<void> => {
    const configs = await this.settingsApiService
      .getAPIConfig<AWIAPIConfig[]>(EExternalAPI.AWI).catch();

    console.log(configs);

    configs[0].accounts.forEach((a) => {
      this.apiAccounts.awi.push({
        name: a.name,
        id: a.accountId,
        publish: false,
      });
    });
  };

  public publishVehicle = async (): Promise<void> => {
    if (!this.formData?.onlinePrice) return;
    if (!this.formData) return;

    this.formData.sending = true;

    /** Parse data from `formData` into `commonExport` */
    const commonExport: CommonExportApiData = { ...this.formCommonApiData };

    commonExport.deliveryPeriod = this.formData.deliveryPeriod
      ? parseInt(String(this.formData.deliveryPeriod), 10)
      : null;
    commonExport.deliveryDate = this.formData.deliveryDate
      ? this.numberConversionService.frontendStringToDate(this.formData.deliveryDate)
      : null;

    /** Step 1: Update common API data and `onlinePrice` on `AutoboardVehicle` */
    await this.salesApiService.putVehicle({
      _id: this.vehicle._id,
      vehicleData: {
        price: {
          onlinePrice: this.numberConversionService.convertFloatToBackend(this.formData.onlinePrice),
        },
      },
      apiData: {
        commonExport,
        currentlyExporting: true,
      },
    } as DeepPartial<AutoboardVehicle>)
      .catch((e) => {
        this.toastService.toast(ToastType.ERROR, 'Fehler beim Speichern des Fahrzeugs.');
        this.logService.error(e);
        this.formData!.sending = false;
      });

    /** Step 2: Create Export request and send it to the server. */
    const req: VehicleExportFrontendRequest = {
      vehicleId: String(this.vehicle._id),
      externalApis: {
        [EExternalAPI.MOBILE_SELLER]: this.apiAccounts.mobileSeller.filter((a) => a.publish).map((a) => ({ accountId: a.id, mode: 'publish' })),
        [EExternalAPI.AUTOSCOUT24]: this.apiAccounts.autoScout24.filter((a) => a.publish).map((a) => ({ accountId: a.id, mode: 'publish' })),
        [EExternalAPI.AWI]: this.apiAccounts.awi.filter((a) => a.publish).map((a) => ({ accountId: a.id, mode: 'publish' })),
      },
    };

    /** Find accounts, that where published before, but now
     * should be unpublished, because the user de-selected them */
    this.vehicle.apiData?.AUTOSCOUT24?.filter((a) => this.apiAccounts.autoScout24
      .find((a2) => a2.id === a.customerId && !a2.publish)).map((a) => a.customerId)
      .forEach((accountId) => req.externalApis[EExternalAPI.AUTOSCOUT24]?.push({ accountId, mode: 'unpublish' }));

    this.vehicle.apiData?.MOBILE_SELLER?.filter((a) => this.apiAccounts.mobileSeller
      .find((a2) => a2.id === a.sellerId && !a2.publish)).map((a) => a.sellerId)
      .forEach((accountId) => req.externalApis[EExternalAPI.MOBILE_SELLER]?.push({ accountId, mode: 'unpublish' }));

    this.vehicle.apiData?.AWI?.filter((a) => this.apiAccounts.awi
      .find((a2) => a2.id === a.accountId && !a2.publish)).map((a) => a.accountId)
      .forEach((accountId) => req.externalApis[EExternalAPI.AWI]?.push({ accountId, mode: 'unpublish' }));

    await this.platformsApiService
      .publishVehicle(req)
      .catch((e) => {
        this.toastService.toast(ToastType.ERROR, 'Fehler beim Veröffentlichen des Fahrzeugs.');
        this.logService.error(e);
        this.formData!.sending = false;
      });
    this.exit({ removeLock: false });
  };

  private addOpsToDescription = (ops: Op[]): void => {
    if (!this.formData) return;
    if (!this.formCommonApiData.description.raw) return;

    if (!(this.formCommonApiData.description.raw as unknown as Delta).ops) {
      (this.formCommonApiData.description.raw as unknown as Delta).ops = [];
    }
    (this.formCommonApiData.description.raw as unknown as Delta).ops.push(...ops);

    this.formData.showDescription = false;
    setTimeout(() => {
      this.formData!.showDescription = true;
    }, 0);
  };

  /** This method adds the `Vehicle`'s `modelData` as text to
   * the end of the description input. */
  public addModelDescription = (): void => this.addOpsToDescription(
    getModelDataAsOps(this.vehicle),
  );

  public addSeriesOptions = (): void => this.addOpsToDescription(
    getSeriesOptionsAsOps(this.vehicle),
  );

  public addAdditionalOptions = (): void => this.addOpsToDescription(
    getAdditionalOptionsAsOps(this.vehicle),
  );

  public addHighlight = (): number => {
    if (!this.formCommonApiData.highlights) this.formCommonApiData.highlights = [];
    return this.formCommonApiData.highlights?.push('');
  };

  public removeHighlight = (index: number): void => {
    if (!this.formCommonApiData.highlights) return;
    this.formCommonApiData.highlights = this.formCommonApiData.highlights.splice(index);
  };
}
